import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { SidebarNext, SidebarTitleItemNext, SidebarDividerNext } from '@wix/design-system';
import { Redirect } from 'react-router';
import { defaultTo, find, isNil, keys, map, pipe, propEq, reduceWhile } from 'ramda';
import { isFalsy } from 'ramda-adjunct';
import { NativeTypes } from 'react-dnd-html5-backend';
import { FormattedMessage, useIntl } from 'react-intl';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import classNames from 'classnames';
import { Button, DropZone, ModalHeader } from '../../components/common';
import { RootModal } from '../../components/common/RootModal';
import { BiAddMediaActionTarget, BiAddMediaClickOrigin } from '../../constants/Bi';
import { DragType } from '../../constants/DragType';
import { HELP_LINKS } from '../../constants/HelpLinks';
import { ModalName } from '../../constants/ModalName';
import { ExternalSource, MainSource } from '../../constants/Source';
import { parentConnection, ParentConnectionEvent } from '../../services/parentConnection';
import { useMMGRSettings } from '../../utils/hoc/withMMGRSettings';
import { useBILoggerTabNameAndLayout } from '../../utils/hooks/bi/useBILoggerTabNameAndLayout';
import { FileTypeConsumer, FileTypeProvider } from '../../utils/hooks/bi/useFileType';
import { useTrackAddMediaButtonClickAnalytics } from '../../utils/hooks/bi/useTrackAddMediaButtonClickAnalytics';
import { useTrackFilesImportPopup } from '../../utils/hooks/bi/useTrackFilesImportPopup';
import { ClickOrigin, useTrackTrashActions } from '../../utils/hooks/bi/useTrackTrashActions';
import { useTrackUploadQueue } from '../../utils/hooks/bi/useTrackUploadQueue';
import { useStorageQuotaAwareHandler } from '../../utils/hooks/storageQuota/useStorageQuotaAwareHandler';
import { StorageQuotaUseCase } from '../../utils/hooks/storageQuota/useStorageQuotaAwareHandler/StorageQuotaUseCase';
import { useStorageFullModal } from '../../utils/hooks/storageQuota/useStorageFullModal';
import { BoardsProvider } from '../../utils/hooks/useBoards';
import { useDropOnSidebar } from '../../utils/hooks/useDropOnSidebar';
import { useUploadFilesHandlers, useUploadingFiles, useUploadQueueHandlers } from '../../utils/hooks/useFilesUploader';
import { useRedirectAfterFirstFileUploaded } from '../../utils/hooks/useFilesUploader/useRedirectAfterFirstFileUploaded';
import { useMediaStudio } from '../../utils/hooks/useMediaStudio';
import { useModal, useModalHandlersAndOptions } from '../../utils/hooks/useModal';
import { RouteBuilder } from '../../utils/RouteHelpers';
import { areSomeMediaTypesUnselected } from '../../utils/uploadingFile';
import { useHelpLink } from '../../components/common/HelpLink/useHelpModal';
import { useHelpWidget } from '../../components/common/HelpLink/useHelpWidget';
import { FilesImporter } from '../FilesImporter';
import { FilesManager } from '../FilesManager';
import { useEmitUploadProgressToParent } from './hooks/useEmitUploadProgressToParent';
import { useForceCloseUploadWindow } from './hooks/useForceCloseUploadWindow';
import { useIsAddMediaButtonVisible } from './hooks/useIsAddMediaButtonVisible';
import { useShutterstockDisabledModal } from './hooks/useShutterstockDisabledModal';
import { UploadWindow } from './UploadWindow';
import { MediaStorageWidget } from './MediaStorageWidget';
import { SideBarLink } from './SideBarLink';
import { AIImageCreationSideBarButton } from './AIImageCreationSideBarButton';
import { useClipboard } from '../../utils/hooks/useClipboard/useClipboard';
import { AddSmall } from '../../components/common/WixIcons';
import { Theme } from '../../utils/hooks/useModal/useModal.types';
import { IInner, IOuter } from './Root.types';
import { MediaManagerSDKPublicMediaFolder } from '../../utils/hoc/withPublicMedia/withPublicMedia.types';
import styles from './Root.scss';

const acceptsMediaManagerItems = [DragType.Folder, DragType.File];
const acceptsNativeFiles = [NativeTypes.FILE];

export const RootComponent: FC<IOuter & IInner> = ({
  source,
  searchQuery,
  items,
  nextPageItems,
  itemsFilters,
  resetFilesStream,
  sort,
  layout,
  settingsLayout,
  mediaType,
  mediaTypes,
  folderPath,
  currentFolder,
  publicMediaCurrentCategory,
  isLoading,
  hasMoreItems,
  areNextFilesLoading,
  folderId,
  title,
  importToSiteMP,
  openFolder,
  openFolderById,
  openJustCreatedFolder,
  openRootFolder,
  refreshFolder,
  moveItemsToTrashMP,
  restoreItemsFromTrashMP,
  updateItemMP,
  copyItemsMP,
  moveItemsMP,
  fetchUserInfoMP,
  emptyTrashMP,
  deleteForeverMP,
  createNewFolderMP,
  getDownloadUrlMP,
  privateItemInfoMP,
  publicItemsInfoMP,
  setMediaType,
  selectAllMediaTypes,
  setSort,
  setLayout,
  loadMoreItems,
  isSourceEnabled,
  layouts,
  navigateTo,
  clearAllExtendedFilters,
  listItemsMP,
  listFoldersMP,
  createNewBoardMP,
  updateBoardMP,
  removeBoardsMP,
  createZipMP,
  publicMediaFoldersTreeMP,
  searchItemsMP,
  searchFoldersMP,
  isFilesImporterModalOpen,
  isEmptyRoute,
  demonstrationThumbnailStyle,
  isRecommended,
  areNotAllMediaTypesSelected,
  restorePreviousMediaTypes,
  isNotAuthorized,
  publicMediaFolderToOpen,
  unsplashDisclaimer,
  selectRecommendedCategories,
  recommendedCategories,
  recommendedCategoriesError,
  isRecommendedCategoriesLoading,
  updateFolderInPath,
  loadingError,
  openedExternal,
  children,
  publicMediaRootCategories,
  decodedFolderId,
}) => {
  useBILoggerTabNameAndLayout({ source, layout });

  const { formatMessage } = useIntl();

  const { openMediaStudio } = useMediaStudio();
  const { mmgrSettings } = useMMGRSettings();
  const {
    customData,
    siteFilesTranslationId,
    addMediaButtonTranslationId,
    isDisableUploadExperimentEnabled,
    isUIAlignmentsEnabled,
    isFileAwarenessEnabled,
    enterpriseTabs = [],
  } = mmgrSettings;

  const storageQuotaAware = useStorageQuotaAwareHandler();

  const hasDataForRecommended = !isNil(customData);

  const { uploadingFiles, hasUploadingFiles, hasAbortableFiles } = useUploadingFiles();

  const { importFiles, uploadFiles } = useUploadFilesHandlers({ source, folderPath });
  const { cancelAllUploads, clearUploadingQueue } = useUploadQueueHandlers();

  const { isUploadWindowForceClosed, forceCloseUploadWindow } = useForceCloseUploadWindow();
  const { isAddMediaButtonVisible } = useIsAddMediaButtonVisible();

  const { trackAddMediaButtonClickSideBar } = useTrackAddMediaButtonClickAnalytics();

  const { trackAddMediaImportFilesPopUpActions } = useTrackFilesImportPopup();
  const { openModal: _openFilesImporterModal, closeModal: closeFilesImporterModal } = useModalHandlersAndOptions(
    ModalName.FilesImporter,
  );
  const openFilesImporterModal = storageQuotaAware(_openFilesImporterModal, StorageQuotaUseCase.UPLOAD);

  const { onHelpClick: onImporterHelpClick, helpModalComponent: importerHelpModal } = useHelpLink(
    HELP_LINKS.IMPORT_FILES,
  );
  const { helpWidgetModal } = useHelpWidget();
  const filesImporterModal = useModal(ModalName.FilesImporter, {
    customLayout: true,
    width: '844px',
    title: formatMessage({ id: 'files-importer.modal-title-new-flow' }),
    onHelpButtonClick: onImporterHelpClick,
    content: ({ additionalOptions: { folderPath } }) => (
      <FilesImporter
        onSelect={(...args) => {
          importFiles(...args);
          closeFilesImporterModal();
        }}
        contentClassName={styles.content}
        {...(folderPath ? { folderPath } : {})}
      />
    ),
    hideFooter: true,
    shouldCloseOnOverlayClick: true,
    noBodyPadding: true,
    confirmText: null,
    cancelText: null,
    onOpen: ({ defaultSource: selectedSource = ExternalSource.Computer }) =>
      trackAddMediaImportFilesPopUpActions({ isOpen: true, tabName: selectedSource }),
    onCloseWithCloseButtonClick: ({ selectedSource }) =>
      trackAddMediaImportFilesPopUpActions({ isOpen: false, tabName: selectedSource }),
    onCloseWithOverlayClick: ({ selectedSource }) =>
      trackAddMediaImportFilesPopUpActions({ isOpen: false, tabName: selectedSource }),
  });

  useEffect(() => {
    if (openedExternal) {
      openFilesImporterModal({ defaultSource: openedExternal });
    }
  }, []);

  const closeMediaManager = useCallback(() => parentConnection.emit(ParentConnectionEvent.Close), []);

  const { closeModal: closeCancelAllUploadingModal } = useModalHandlersAndOptions(ModalName.CancelAllUploads);
  const cancelAllUploadingModal = useModal(ModalName.CancelAllUploads, {
    content: <FormattedMessage id="root.cancel-all-modal-content" />,
    title: <ModalHeader title={<FormattedMessage id="root.cancel-all-modal-title" />} />,
    confirmText: formatMessage({ id: 'common.yes' }),
    cancelText: formatMessage({ id: 'common.no' }),
    onOk: cancelAllUploads,
    skin: Theme.Destructive,
  });

  const shutterstockDisabledModal = useShutterstockDisabledModal();
  const fullStorageModal = useStorageFullModal();

  useEffect(() => {
    if (!hasAbortableFiles) {
      closeCancelAllUploadingModal();
    }
  }, [hasAbortableFiles, closeCancelAllUploadingModal]);

  useEffect(() => {
    if (mmgrSettings.addMediaPanelOpened) {
      openFilesImporterModal({ openedFrom: BiAddMediaClickOrigin.AutoOpen });
    }
  }, [mmgrSettings.addMediaPanelOpened, openFilesImporterModal]);

  useEmitUploadProgressToParent();

  const clipboardProps = useClipboard();

  const { handleDropOnSidebar } = useDropOnSidebar();

  const { trackMoveToTrash } = useTrackTrashActions();

  const handleDropOnTrashInSideBar = useCallback(
    (items) => {
      trackMoveToTrash(items, { clickOrigin: ClickOrigin.DragDrop });
      handleDropOnSidebar(MainSource.Trash, items);
    },
    [handleDropOnSidebar, trackMoveToTrash],
  );

  const handleAddMediaButtonClick = useCallback(() => {
    openFilesImporterModal({ openedFrom: BiAddMediaClickOrigin.SideBar });
    trackAddMediaButtonClickSideBar();
  }, [openFilesImporterModal, trackAddMediaButtonClickSideBar]);

  const openUploadingDestinationFolder = useCallback(
    (folderId, source, fileId) => {
      const hasUnselectedMediaTypes = areSomeMediaTypesUnselected(uploadingFiles, mediaType);

      if (hasUnselectedMediaTypes) {
        selectAllMediaTypes();
      }

      openFolderById(folderId, source, null, fileId);
    },
    [uploadingFiles, openFolderById, mediaType, selectAllMediaTypes],
  );

  const getPublicMediaSubcategoryNameAndRoot = (subcategoryId) =>
    pipe<{ [key in string]: MediaManagerSDKPublicMediaFolder }, string[], [string, string], [string, string]>(
      keys,
      reduceWhile(
        isFalsy,
        (_info, id) => {
          const category = publicMediaRootCategories[id];
          const subCategory = find<MediaManagerSDKPublicMediaFolder>(propEq('id', subcategoryId), category.subFolders);

          return subCategory ? [subCategory.name, category.parentId] : null;
        },
        null,
      ),
      defaultTo([null, null]),
    )(publicMediaRootCategories);

  useRedirectAfterFirstFileUploaded({ openFolderById, source });

  useTrackUploadQueue({ openFolderById, mediaType, selectAllMediaTypes });

  const sidebarHeader = useMemo(
    () => (
      <div className={styles.sidebarHeader}>
        {isAddMediaButtonVisible && (
          <div className={styles.addMediaButtonWrapper}>
            {isUIAlignmentsEnabled ? (
              <Button
                size="small"
                prefixIcon={<AddSmall />}
                disabled={isNotAuthorized || isDisableUploadExperimentEnabled}
                onClick={handleAddMediaButtonClick}
                dataHook="add-media-button"
                fullWidth
              >
                <FormattedMessage id={addMediaButtonTranslationId} />
              </Button>
            ) : (
              <Button
                disabled={isNotAuthorized || isDisableUploadExperimentEnabled}
                size="medium"
                onClick={handleAddMediaButtonClick}
                dataHook="add-media-button"
                fullWidth
              >
                <FormattedMessage id={addMediaButtonTranslationId} />
              </Button>
            )}
          </div>
        )}
      </div>
    ),
    [
      isAddMediaButtonVisible,
      isDisableUploadExperimentEnabled,
      isNotAuthorized,
      addMediaButtonTranslationId,
      handleAddMediaButtonClick,
    ],
  );

  if (isRecommended && !hasDataForRecommended) {
    return <Redirect to={RouteBuilder.Source(MainSource.SiteFiles)} />;
  }

  return (
    <BoardsProvider source={source}>
      <FileTypeProvider
        source={source}
        folderId={folderId}
        getPublicMediaSubcategoryNameAndRoot={getPublicMediaSubcategoryNameAndRoot}
      >
        <RootModal onCancel={closeMediaManager}>
          <SidebarNext
            dataHook="root-sidebar"
            className={classNames(styles.sidebarNext, {
              [styles.new]: isUIAlignmentsEnabled,
            })}
            skin="neutral"
            isLoading={false}
            header={sidebarHeader}
            footer={<MediaStorageWidget />}
            selectedKey={source}
            next={isUIAlignmentsEnabled}
          >
            {isSourceEnabled(MainSource.Home) && (
              <>
                <SideBarLink
                  to={RouteBuilder.Source(MainSource.Home)}
                  source={MainSource.Home}
                  dataHook="side-bar-home-link"
                >
                  <FormattedMessage id="categories.media-home" />
                </SideBarLink>
                <div className={styles.divider}>
                  <SidebarDividerNext />
                </div>
              </>
            )}

            <SidebarTitleItemNext>
              <span className={styles.uppercase}>
                <FormattedMessage id="categories.manage" />
              </span>
            </SidebarTitleItemNext>
            {isSourceEnabled(MainSource.SiteFiles) && (
              <SideBarLink to={RouteBuilder.Source(MainSource.SiteFiles)} source={MainSource.SiteFiles}>
                <FormattedMessage id={siteFilesTranslationId} />
              </SideBarLink>
            )}

            {isSourceEnabled(MainSource.VisitorsUploads) && (
              <SideBarLink to={RouteBuilder.Source(MainSource.VisitorsUploads)} source={MainSource.VisitorsUploads}>
                <FormattedMessage id="categories.visitor-uploads" />
              </SideBarLink>
            )}

            {isSourceEnabled(MainSource.Boards) && (
              <SideBarLink
                to={RouteBuilder.Source(MainSource.Boards)}
                data-hook="side-bar-boards-link"
                source={MainSource.Boards}
              >
                <FormattedMessage id="categories.boards" />
              </SideBarLink>
            )}

            {isSourceEnabled(MainSource.Trash) && (
              <DropZone
                activeClassName={styles.dropZoneActive}
                accepts={acceptsMediaManagerItems}
                onDrop={handleDropOnTrashInSideBar}
                biOrigin={null}
                biTarget={null}
              >
                <SideBarLink
                  to={RouteBuilder.Source(MainSource.Trash)}
                  source={MainSource.Trash}
                  data-hook="side-bar-trash-link"
                >
                  <FormattedMessage id="categories.trash" />
                </SideBarLink>
              </DropZone>
            )}

            <div className={styles.divider}>
              <SidebarDividerNext />
            </div>

            <SidebarTitleItemNext>
              <span className={styles.uppercase}>
                <FormattedMessage id="categories.explore" />
              </span>
            </SidebarTitleItemNext>
            {isSourceEnabled(MainSource.NTT) && (
              <SideBarLink to={RouteBuilder.Source(MainSource.NTT, publicMediaFolderToOpen)} source={MainSource.NTT}>
                <FormattedMessage id="categories.ntt" />
              </SideBarLink>
            )}

            {isSourceEnabled(MainSource.Yell) && (
              <SideBarLink to={RouteBuilder.Source(MainSource.Yell)} source={MainSource.Yell}>
                <FormattedMessage id="categories.yell" />
              </SideBarLink>
            )}

            {isSourceEnabled(MainSource.Recommended) && hasDataForRecommended && (
              <SideBarLink to={RouteBuilder.Source(MainSource.Recommended)} source={MainSource.Recommended}>
                <FormattedMessage id="categories.recommended" />
              </SideBarLink>
            )}

            {isSourceEnabled(MainSource.MediaFromWix) && (
              <SideBarLink
                to={RouteBuilder.Source(MainSource.MediaFromWix, publicMediaFolderToOpen)}
                source={MainSource.MediaFromWix}
              >
                <FormattedMessage id="categories.free-from-wix" />
              </SideBarLink>
            )}

            {(isSourceEnabled(MainSource.Shutterstock) || isSourceEnabled(MainSource.ShutterstockVideo)) && (
              <SideBarLink to={RouteBuilder.Source(MainSource.Shutterstock)} source={MainSource.Shutterstock}>
                <FormattedMessage id="categories.shutterstock" />
              </SideBarLink>
            )}

            {isSourceEnabled(MainSource.Unsplash) && (
              <SideBarLink to={RouteBuilder.Source(MainSource.Unsplash)} source={MainSource.Unsplash}>
                <FormattedMessage id="categories.unsplash" />
              </SideBarLink>
            )}

            {map(
              (enterpriseTab) => (
                <SideBarLink
                  source={MainSource.Enterprise}
                  to={RouteBuilder.EnterpriseTab(enterpriseTab.tabNameKey)}
                  key={enterpriseTab.tabNameKey}
                  data-hook={`side-bar-${enterpriseTab.tabNameKey}-link`}
                >
                  <FormattedMessage id={enterpriseTab.tabNameKey} defaultMessage={enterpriseTab.tabNameEn} />
                </SideBarLink>
              ),
              enterpriseTabs,
            )}

            <AIImageCreationSideBarButton targetFolder={currentFolder} />
          </SidebarNext>

          <TransitionGroup>
            {!isUploadWindowForceClosed && hasUploadingFiles && (
              <CSSTransition classNames={styles} timeout={300}>
                <UploadWindow
                  className={styles.uploadWindow}
                  onOpenDestinationFolder={openUploadingDestinationFolder}
                  onClearUploadingQueue={clearUploadingQueue}
                  onForceClose={forceCloseUploadWindow}
                  currentFolder={currentFolder}
                />
              </CSSTransition>
            )}
          </TransitionGroup>
          {!isEmptyRoute && (
            <FileTypeConsumer>
              {(fileType) => (
                <FilesManager
                  {...clipboardProps}
                  key={source}
                  fileType={fileType}
                  className={styles.content}
                  folderId={folderId}
                  title={title}
                  source={source}
                  searchQuery={searchQuery}
                  items={items}
                  nextPageItems={nextPageItems}
                  itemsFilters={itemsFilters}
                  resetFilesStream={resetFilesStream}
                  sort={sort}
                  mediaType={mediaType}
                  mediaTypes={mediaTypes}
                  areNotAllMediaTypesSelected={areNotAllMediaTypesSelected}
                  layout={layout}
                  settingsLayout={settingsLayout}
                  folderPath={folderPath}
                  currentFolder={currentFolder}
                  publicMediaCurrentCategory={publicMediaCurrentCategory}
                  isLoading={isLoading}
                  hasMoreItems={hasMoreItems}
                  areNextFilesLoading={areNextFilesLoading}
                  openFolder={openFolder}
                  openFolderById={openFolderById}
                  openJustCreatedFolder={openJustCreatedFolder}
                  openRootFolder={openRootFolder}
                  refreshFolder={refreshFolder}
                  moveItemsToTrashMP={moveItemsToTrashMP}
                  restoreItemsFromTrashMP={restoreItemsFromTrashMP}
                  updateItemMP={updateItemMP}
                  copyItemsMP={copyItemsMP}
                  moveItemsMP={moveItemsMP}
                  fetchUserInfoMP={fetchUserInfoMP}
                  emptyTrashMP={emptyTrashMP}
                  deleteForeverMP={deleteForeverMP}
                  createNewFolderMP={createNewFolderMP}
                  getDownloadUrlMP={getDownloadUrlMP}
                  privateItemInfoMP={privateItemInfoMP}
                  publicItemsInfoMP={publicItemsInfoMP}
                  loadMoreItems={loadMoreItems}
                  setMediaType={setMediaType}
                  selectAllMediaTypes={selectAllMediaTypes}
                  restorePreviousMediaTypes={restorePreviousMediaTypes}
                  setSort={setSort}
                  setLayout={setLayout}
                  layouts={layouts}
                  updateFolderInPath={updateFolderInPath}
                  navigateTo={navigateTo}
                  clearAllExtendedFilters={clearAllExtendedFilters}
                  importToSiteMP={importToSiteMP}
                  editImage={openMediaStudio}
                  listItemsMP={listItemsMP}
                  createNewBoardMP={createNewBoardMP}
                  updateBoardMP={updateBoardMP}
                  removeBoardsMP={removeBoardsMP}
                  createZipMP={createZipMP}
                  publicMediaFoldersTreeMP={publicMediaFoldersTreeMP}
                  listFoldersMP={listFoldersMP}
                  searchItemsMP={searchItemsMP}
                  searchFoldersMP={searchFoldersMP}
                  demonstrationThumbnailStyle={demonstrationThumbnailStyle}
                  getPublicMediaSubcategoryNameAndRoot={getPublicMediaSubcategoryNameAndRoot}
                  unsplashDisclaimer={unsplashDisclaimer}
                  selectRecommendedCategories={selectRecommendedCategories}
                  recommendedCategories={recommendedCategories}
                  recommendedCategoriesError={recommendedCategoriesError}
                  isRecommendedCategoriesLoading={isRecommendedCategoriesLoading}
                  loadingError={loadingError}
                  publicMediaRootCategories={publicMediaRootCategories}
                  decodedFolderId={decodedFolderId}
                />
              )}
            </FileTypeConsumer>
          )}
          <DropZone
            className={styles.globalDropZone}
            activeClassName={styles.globalDropZoneActive}
            draggingClassName={styles.globalDropZoneDragging}
            accepts={acceptsNativeFiles}
            onDrop={storageQuotaAware(uploadFiles, StorageQuotaUseCase.UPLOAD)}
            disabled={isDisableUploadExperimentEnabled || isFilesImporterModalOpen}
            biOrigin={BiAddMediaClickOrigin.DragAndDrop}
            biTarget={BiAddMediaActionTarget.UploadDragAndDrop}
            createFolderFn={createNewFolderMP}
            currentFolder={currentFolder}
          />
          {helpWidgetModal}
          {cancelAllUploadingModal}
          {filesImporterModal}
          {shutterstockDisabledModal}
          {importerHelpModal}
          {fullStorageModal}
          {children}
        </RootModal>
      </FileTypeProvider>
    </BoardsProvider>
  );
};
